import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { z } from 'zod';
import { api, diagnoseNoResponse } from '@/lib/api';
import toast from 'react-hot-toast';

const schema = z.object({ email: z.string().email() });
type FormData = z.infer<typeof schema>;

export default function Forgot() {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>();
  const [sentTo, setSentTo] = useState<string | null>(null);

  const [notRegistered, setNotRegistered] = useState<string | null>(null);

  async function onSubmit(values: FormData) {
    setNotRegistered(null);
    try {
      schema.parse(values);
      // The API checks the address against the database and only issues a
      // token for a real account - an unknown email comes back as a 404.
      await api.post('/auth/forgot-password', { email: values.email });
      setSentTo(values.email);
    } catch (e: any) {
      if (e?.name === 'ZodError') return toast.error('Enter a valid email address.');
      if (!e?.response) return toast.error(await diagnoseNoResponse(), { duration: 9000 });

      const { status, data } = e.response;
      if (status === 404 && data?.code === 'EMAIL_NOT_REGISTERED') {
        // Shown inline rather than as a toast: it needs the "create an
        // account" escape hatch beside it.
        return setNotRegistered(values.email);
      }
      if (status === 429) {
        return toast.error('Too many reset requests. Please wait a few minutes and try again.');
      }
      toast.error(data?.message ?? 'Could not send the reset email.', { duration: 9000 });
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-12 overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1920&h=1280&fit=crop&auto=format&q=80"
        alt="" aria-hidden
        onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=1280&fit=crop&auto=format&q=80'; }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/74 to-ink/92" />
      <div className="absolute inset-0 mashrabiya opacity-[0.06] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[400px]">
        <div className="text-center mb-6">
          <Link to="/" className="display text-[44px] leading-none text-gold tracking-tight drop-shadow">Souqli</Link>
          <p className="text-cream/85 text-[15px] mt-3 leading-snug max-w-[32ch] mx-auto">
            {sentTo ? 'Check your inbox.' : 'Forgotten your password? We’ll email you a link to set a new one.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-frame border border-warmline p-5 sm:p-6">
          {sentTo ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-sage/15 grid place-items-center mx-auto mb-4 text-2xl">✉️</div>
              <h1 className="display text-[22px] mb-2">Reset link sent</h1>
              <p className="text-[14px] text-mute leading-relaxed mb-4">
                We’ve emailed a reset link to <span className="font-medium text-ink break-all">{sentTo}</span>.
                It expires in 10 minutes and can only be used once.
              </p>
              <p className="text-[13px] text-mute leading-relaxed mb-5">
                Nothing after a minute or two? Check your spam folder - the email comes from Souqli
                and may be filtered the first time.
              </p>
              <button onClick={() => setSentTo(null)} className="btn btn-outline btn-sm w-full mb-2">
                Use a different email
              </button>
              <Link to="/login" className="btn btn-primary btn-lg w-full text-center">Back to log in</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              {notRegistered && (
                <div className="rounded-luxe bg-bad/5 border border-bad/25 px-3.5 py-3 mb-1">
                  <div className="flex items-start gap-2.5">
                    <span className="text-[15px] leading-none mt-0.5">⚠️</span>
                    <div className="min-w-0">
                      <p className="text-[13px] text-ink leading-relaxed">
                        No Souqli account uses <span className="font-medium mono break-all">{notRegistered}</span>.
                        Check the spelling, or create an account with it.
                      </p>
                      <Link to="/register" className="inline-block mt-2 text-[13px] text-gold-dark font-semibold hover:underline">
                        Create an account →
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">YOUR EMAIL ADDRESS</label>
                <input
                  className="input input-lg"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="you@example.com"
                  {...register('email', { required: true })}
                />
                {errors.email && <p className="text-bad text-xs mt-1">Valid email required</p>}
              </div>

              <button disabled={isSubmitting} className="btn btn-lg btn-primary w-full !mt-4">
                {isSubmitting ? 'Sending…' : 'Email me a reset link'}
              </button>

              <div className="flex items-center gap-3 text-mute my-4">
                <div className="flex-1 h-px bg-warmline" />
                <span className="stamp text-[9px]">or</span>
                <div className="flex-1 h-px bg-warmline" />
              </div>

              <Link to="/login" className="btn btn-lg w-full text-center bg-sage-dark text-purewhite border-0 hover:brightness-110 transition">
                Back to log in
              </Link>
            </form>
          )}
        </div>

        <p className="text-center text-[13px] text-cream/70 mt-6">
          No account yet? <Link to="/register" className="text-gold font-semibold hover:underline">Create one</Link>.
        </p>
      </div>
    </div>
  );
}
